/**
 * Days left on the monthly platform fee — pure, so it can be checked without a
 * browser. Feeds the "Platform Fee" block in the Dashboard usage card.
 *
 * Same maths as the Buy Credits modal (`Math.ceil` of the remaining days), so
 * the two screens can never disagree about how many days are left.
 *
 * Paid days STACK (renewing early adds 30 days on top), so `daysLeft` can be
 * more than 30. The bar is "share of one 30-day period left" and simply stays
 * full above 30.
 */

export const PLATFORM_PERIOD_DAYS = 30;
/** Gold at a week left, red at three days — time to renew before calls stop. */
export const WARN_DAYS = 7;
export const DANGER_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PlatformFeeStatus {
  /** never = no paid-through date on the account at all. */
  state: 'active' | 'expired' | 'never';
  /** Whole days left, rounded UP. 0 when not active. */
  daysLeft: number;
  /** 0-100: share of one 30-day period remaining. */
  percent: number;
  level: 'ok' | 'warn' | 'danger';
  /** Paid-through (or ended) date as YYYY-MM-DD in Central time, else null. */
  dateKey: string | null;
}

/** YYYY-MM-DD in America/Chicago — the same form as the "Today" label beside it. */
export function centralDateKey(ms: number): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(ms));
}

export function platformFeeStatus(paidUntilMs: number | null | undefined, nowMs: number): PlatformFeeStatus {
  if (typeof paidUntilMs !== 'number' || !Number.isFinite(paidUntilMs)) {
    return { state: 'never', daysLeft: 0, percent: 0, level: 'danger', dateKey: null };
  }
  const dateKey = centralDateKey(paidUntilMs);
  if (paidUntilMs <= nowMs) {
    return { state: 'expired', daysLeft: 0, percent: 0, level: 'danger', dateKey };
  }
  const daysLeft = Math.ceil((paidUntilMs - nowMs) / DAY_MS);
  const level = daysLeft <= DANGER_DAYS ? 'danger' : daysLeft <= WARN_DAYS ? 'warn' : 'ok';
  return {
    state: 'active',
    daysLeft,
    percent: Math.min(100, Math.round((daysLeft / PLATFORM_PERIOD_DAYS) * 100)),
    level,
    dateKey,
  };
}
